import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { toMessage } from "@/api/errors";
import {
  addFolderMember,
  listFolderMembers,
  listUsers,
  removeFolderMember,
} from "@/api/lookups";
import {
  type Folder,
  type FolderMember,
  type UUID,
  type UserListItem,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

/**
 * `FolderMembersPanel` — admin-only sub-panel inside the FolderDrawer.
 *
 * Renders the list of users assigned to one folder and lets the admin
 * add or remove a user. The component owns its own
 * `useQuery({ queryKey: ["folder-members", folderId] })` so it can
 * refetch independently of the parent registry.
 *
 * Variant A (2026-05-22): the per-folder role concept was retired
 * together with migration 0109 — the table is now a flat
 * `(user, remove button)` list. The «Изменить роль» control is gone.
 *
 * RBAC: every mutation handler is admin-only on the backend; the page
 * mounts this panel only when `role === "admin"`. The component still
 * checks `disabled` flags defensively so the buttons never fire on a
 * read-only render (e.g. dev preview).
 */
interface FolderMembersPanelProps {
  folder: Folder;
}

export function FolderMembersPanel({ folder }: FolderMembersPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  // Per-row removal feedback. Keyed by user_id so two rows can fail
  // independently. Empty string ⇒ "no error".
  const [removeError, setRemoveError] = useState<Record<string, string>>({});

  const members = useQuery({
    queryKey: ["folder-members", folder.id],
    queryFn: () => listFolderMembers(folder.id),
  });

  // Users registry is the source for "which user can I add?". We use the
  // shared key `["users", "list"]` so the UsersAdmin page's existing
  // cache is reused when both pages are mounted in sequence.
  const users = useQuery({
    queryKey: ["users", "list"],
    queryFn: () => listUsers(),
  });

  const invalidateMembers = () =>
    queryClient.invalidateQueries({
      queryKey: ["folder-members", folder.id],
    });

  const removeMutation = useMutation({
    mutationFn: (userId: UUID) => removeFolderMember(folder.id, userId),
    onSuccess: invalidateMembers,
  });

  // Pre-build a `user_id → user` map so the rendering side does not
  // re-scan the user list on every row.
  const userMap = useMemo(() => {
    const map: Record<string, UserListItem> = {};
    for (const u of users.data ?? []) map[u.id] = u;
    return map;
  }, [users.data]);

  const memberRows: FolderMember[] = members.data ?? [];

  const handleRemove = (m: FolderMember) => {
    const confirmKey = folder.is_default
      ? "folders.admin.removeConfirmDefault"
      : "folders.admin.removeConfirm";
    const ok = window.confirm(t(confirmKey));
    if (!ok) return;
    setRemoveError((prev) => ({ ...prev, [m.user_id]: "" }));
    removeMutation.mutate(m.user_id, {
      onError: (err: unknown) => {
        setRemoveError((prev) => ({
          ...prev,
          [m.user_id]: toMessage(
            err,
            t("folders.admin.errors.removeMemberFailed"),
          ),
        }));
      },
    });
  };

  return (
    <section className="space-y-3" data-testid="folder-members-panel">
      <header className="flex items-end justify-between gap-2">
        <h3 className="text-base font-semibold">
          {t("folders.admin.membersHeader")}
        </h3>
        <Button
          type="button"
          size="sm"
          onClick={() => setAdding(true)}
          data-testid="open-add-member"
        >
          {t("folders.admin.addMember")}
        </Button>
      </header>

      {members.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : memberRows.length === 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="folder-members-empty"
        >
          {t("folders.admin.noMembers")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm" data-testid="folder-members-table">
            <thead className="bg-muted/40">
              <tr>
                <th className="border-b px-3 py-2 text-left font-medium">
                  {t("folders.admin.memberColumns.user")}
                </th>
                <th className="border-b px-3 py-2 text-left font-medium">
                  {t("folders.admin.memberColumns.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {memberRows.map((m) => {
                const u = userMap[m.user_id];
                const rowErr = removeError[m.user_id] ?? "";
                return (
                  <tr
                    key={m.user_id}
                    data-testid={`folder-member-row-${m.user_id}`}
                  >
                    <td className="border-b px-3 py-2 align-top">
                      <span data-testid={`folder-member-email-${m.user_id}`}>
                        {u?.employee_display ?? u?.email ?? m.user_id}
                      </span>
                    </td>
                    <td className="border-b px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemove(m)}
                          disabled={removeMutation.isPending}
                          data-testid={`folder-member-remove-${m.user_id}`}
                        >
                          {t("folders.admin.removeMember")}
                        </Button>
                      </div>
                      {rowErr !== "" ? (
                        <p
                          className="mt-1 text-xs text-destructive"
                          role="alert"
                          data-testid={`folder-member-remove-error-${m.user_id}`}
                        >
                          {rowErr}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AddMemberDialog
        open={adding}
        onClose={() => setAdding(false)}
        folderId={folder.id}
        // Filter out users already in the folder so the "add" picker only
        // shows candidates whose membership would actually change.
        candidateUsers={(users.data ?? []).filter(
          (u) =>
            !memberRows.some((m) => m.user_id === u.id) &&
            u.status !== "invited",
        )}
        onAdded={() => {
          setAdding(false);
          void invalidateMembers();
        }}
      />
    </section>
  );
}

interface AddMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  folderId: UUID;
  candidateUsers: UserListItem[];
}

function AddMemberDialog({
  open,
  onClose,
  onAdded,
  folderId,
  candidateUsers,
}: AddMemberDialogProps) {
  const { t } = useTranslation();
  const [userId, setUserId] = useState<UUID | "">("");

  const mutation = useMutation({
    mutationFn: () => {
      if (userId === "") throw new Error("user_id is empty");
      return addFolderMember(folderId, { user_id: userId });
    },
    onSuccess: () => {
      setUserId("");
      onAdded();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("folders.admin.addMember")}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          data-testid="folder-member-add-form"
        >
          <div>
            <Label htmlFor="folder-member-user">
              {t("folders.admin.userLabel")}
            </Label>
            <select
              id="folder-member-user"
              data-testid="folder-member-user-select"
              value={userId}
              onChange={(e) => setUserId(e.target.value as UUID | "")}
              className="mt-1 w-full rounded-md border px-2 py-1 text-sm"
              required
            >
              <option value="">—</option>
              {candidateUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.employee_display !== null &&
                  u.employee_display !== undefined
                    ? `${u.employee_display} (${u.email})`
                    : u.email}
                </option>
              ))}
            </select>
          </div>
          {mutation.isError ? (
            <p
              className="text-xs text-destructive"
              role="alert"
              data-testid="folder-member-add-error"
            >
              {toMessage(
                mutation.error,
                t("folders.admin.errors.addMemberFailed"),
              )}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || userId === ""}
              data-testid="submit-add-member"
            >
              {mutation.isPending
                ? t("common.saving")
                : t("folders.admin.addMember")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
